import React, { useEffect, useState } from "react";
import api from "../api";
import { fmtCLP, fmtFecha } from "../utils/formato";
import toast from "../utils/toast";
import "./ArqueosCaja.css";

const DENOMINACIONES = [
  { valor: 20000, label: "$20.000" },
  { valor: 10000, label: "$10.000" },
  { valor: 5000, label: "$5.000" },
  { valor: 2000, label: "$2.000" },
  { valor: 1000, label: "$1.000" },
  { valor: 500, label: "$500" },
  { valor: 100, label: "$100" },
  { valor: 50, label: "$50" },
  { valor: 10, label: "$10" },
];

export default function ArqueosCaja({ usuario }) {
  const [arqueos, setArqueos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [mostrarForm, setMostrarForm] = useState(false);
  const [montoEsperado, setMontoEsperado] = useState("");
  const [montoContado, setMontoContado] = useState("");
  const [observacion, setObservacion] = useState("");
  const [desglose, setDesglose] = useState(() =>
    DENOMINACIONES.reduce((acc, d) => ({ ...acc, [d.valor]: "" }), {})
  );
  const [enviando, setEnviando] = useState(false);

  const totalDesglose = DENOMINACIONES.reduce((sum, d) => {
    const q = parseInt(desglose[d.valor], 10) || 0;
    return sum + d.valor * q;
  }, 0);

  const cargar = () => {
    setCargando(true);
    setError(null);
    const params = { limit: 50 };
    if (usuario?.rol !== "superadmin" && usuario?.id) params.usuario_id = usuario.id;
    api.get("/api/caja/arqueos", { params })
      .then(({ data }) => setArqueos(data?.rows || []))
      .catch((e) => setError(e?.response?.data?.error || e.message || "Error al cargar"))
      .finally(() => setCargando(false));
  };

  useEffect(() => {
    cargar();
  }, [usuario?.id, usuario?.rol]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const esperado = parseFloat(String(montoEsperado).replace(",", ".")) || 0;
    const contado = parseFloat(String(montoContado).replace(",", ".")) || 0;
    const desgloseParaEnviar = {};
    DENOMINACIONES.forEach((d) => {
      const q = parseInt(desglose[d.valor], 10) || 0;
      if (q > 0) desgloseParaEnviar[d.valor] = q;
    });
    setEnviando(true);
    api.post("/api/caja/arqueos", {
      usuario_id: usuario.id,
      monto_esperado: esperado,
      monto_contado: contado,
      desglose_contado: Object.keys(desgloseParaEnviar).length ? desgloseParaEnviar : null,
      observacion: (observacion || "").trim() || null,
    })
      .then(() => {
        setMontoEsperado("");
        setMontoContado("");
        setObservacion("");
        setDesglose(DENOMINACIONES.reduce((acc, d) => ({ ...acc, [d.valor]: "" }), {}));
        setMostrarForm(false);
        cargar();
      })
      .catch((e) => toast(e?.response?.data?.error || "Error al registrar arqueo"))
      .finally(() => setEnviando(false));
  };

  return (
    <div className="ac-page">
      <header className="ac-header">
        <h1>Arqueos de caja</h1>
        <p className="ac-desc">
          Registro de conteo físico de efectivo vs. monto esperado (diferencia).
        </p>
      </header>

      <button type="button" className="ac-btn primary" onClick={() => setMostrarForm(true)}>
        + Nuevo arqueo
      </button>

      {error && <div className="ac-alert error">{error}</div>}

      {mostrarForm && (
        <div className="ac-overlay" onClick={() => !enviando && setMostrarForm(false)}>
          <div className="ac-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Nuevo arqueo</h3>
            <form onSubmit={handleSubmit}>
              <label>
                Monto esperado ($)
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={montoEsperado}
                  onChange={(e) => setMontoEsperado(e.target.value)}
                  required
                />
              </label>
              <label>
                Monto contado ($)
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={montoContado}
                  onChange={(e) => setMontoContado(e.target.value)}
                  required
                />
              </label>
              {montoEsperado !== "" && montoContado !== "" && (
                <p className="ac-diferencia">
                  Diferencia:{" "}
                  <strong className={(parseFloat(String(montoContado).replace(",", ".")) || 0) - (parseFloat(String(montoEsperado).replace(",", ".")) || 0) !== 0 ? "ac-diff" : ""}>
                    {fmtCLP((parseFloat(String(montoContado).replace(",", ".")) || 0) - (parseFloat(String(montoEsperado).replace(",", ".")) || 0))}
                  </strong>
                </p>
              )}
              <div className="ac-desglose">
                <span className="ac-desglose-title">Desglose contado (opcional)</span>
                <div className="ac-desglose-grid">
                  {DENOMINACIONES.map((d) => (
                    <div key={d.valor} className="ac-desglose-row">
                      <label>{d.label}</label>
                      <input
                        type="number"
                        min="0"
                        value={desglose[d.valor]}
                        onChange={(e) =>
                          setDesglose((prev) => ({ ...prev, [d.valor]: e.target.value.replace(/\D/g, "").slice(0, 6) }))
                        }
                      />
                    </div>
                  ))}
                </div>
                <p className="ac-total-desglose">Total desglose: <strong>{fmtCLP(totalDesglose)}</strong></p>
              </div>
              <label>
                Observación (opcional)
                <input
                  type="text"
                  value={observacion}
                  onChange={(e) => setObservacion(e.target.value)}
                  placeholder="Ej: Faltante por error en vuelto"
                />
              </label>
              <div className="ac-modal-actions">
                <button type="button" onClick={() => setMostrarForm(false)} disabled={enviando}>Cancelar</button>
                <button type="submit" className="primary" disabled={enviando}>
                  {enviando ? "Guardando…" : "Guardar arqueo"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {cargando ? (
        <div className="ac-empty">Cargando arqueos…</div>
      ) : !arqueos.length ? (
        <div className="ac-empty">No hay arqueos registrados.</div>
      ) : (
        <div className="ac-table-wrap">
          <table className="ac-table">
            <thead>
              <tr>
                <th>Fecha / Hora</th>
                {usuario?.rol === "superadmin" && <th>Usuario</th>}
                <th className="right">Esperado</th>
                <th className="right">Contado</th>
                <th className="right">Diferencia</th>
                <th>Observación</th>
              </tr>
            </thead>
            <tbody>
              {arqueos.map((a) => {
                const diff = Number(a.diferencia ?? 0);
                return (
                  <tr key={a.id}>
                    <td>{fmtFecha(a.creado_en)}</td>
                    {usuario?.rol === "superadmin" && <td>{a.usuario_nombre ?? `ID ${a.usuario_id}`}</td>}
                    <td className="right">{fmtCLP(a.monto_esperado)}</td>
                    <td className="right">{fmtCLP(a.monto_contado)}</td>
                    <td className={`right ${diff !== 0 ? "ac-diff" : ""}`}>{fmtCLP(diff)}</td>
                    <td>{a.observacion || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
